import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { postJson, setUnauthorizedHandler } from '../api/client';

import { AuthContext } from './AuthContextValue';
import type { AuthState } from './AuthContextValue';
import { clearSession, readSession, writeSession } from './token';
import type { StoredSession } from './token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(() => readSession());

  const signOut = useCallback(() => {
    clearSession();
    setSession(null);
  }, []);

  // The client cannot import React, so it calls back into here instead.
  useEffect(() => {
    setUnauthorizedHandler(() => setSession(null));

    return () => setUnauthorizedHandler(null);
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    const result = await postJson<StoredSession>('/auth/login', { username, password });

    writeSession(result);
    setSession(result);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      username: session?.username ?? null,
      isAuthenticated: session !== null,
      signIn,
      signOut,
    }),
    [session, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
